import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { Button } from '../../Button';
import { CloseButton } from '../../CloseButton';
import { ThemeIcon } from '../../ThemeIcon';
import { DialogShell } from '../_DialogShell';
import styles from './AlertDialog.module.css';

export type AlertDialogVariant = 'negative' | 'regular' | 'positive';

export interface AlertDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback fired when the dialog is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Visual variant — controls ThemeIcon color and primary button style */
  variant?: AlertDialogVariant;
  /** Dialog title text */
  title: string;
  /** Primary body text */
  description?: string;
  /** Secondary (dimmed) body text shown below description */
  secondaryText?: string;
  /** Extra content (e.g. form controls) rendered below the body text */
  children?: ReactNode;
  /** Label for the primary CTA button */
  primaryLabel: string;
  /** Callback fired when the primary CTA is clicked */
  onPrimaryAction?: () => void;
  /** Whether the primary CTA shows a loading state */
  isPrimaryLoading?: boolean;
  /** Label for the secondary (tertiary) CTA button — omit to hide */
  secondaryLabel?: string;
  /** Callback fired when the secondary CTA is clicked */
  onSecondaryAction?: () => void;
  /** Whether to show the ThemeIcon above the title */
  withIcon?: boolean;
  /** Whether the dialog is centered vertically */
  isCentered?: boolean;
  /** Dialog content width */
  size?: string | number;
  /** Whether to close the dialog when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the dialog when pressing Escape */
  shouldCloseOnEscape?: boolean;
  className?: string;
}

const THEME_ICON_COLOR = {
  negative: 'red',
  regular: 'indigo',
  positive: 'green',
} as const;

const THEME_ICON_COMPONENT = {
  negative: IconAlertCircle,
  regular: IconAlertCircle,
  positive: IconCheck,
} as const;

/**
 * AlertDialog — status alert built on the shared Dialog shell. Mirrors
 * ModalMessage (optional status icon, negative/regular/positive variants,
 * right-aligned tertiary + primary actions) at a larger scale.
 */
export function AlertDialog({
  isOpen,
  onClose,
  variant = 'regular',
  title,
  description,
  secondaryText,
  children,
  primaryLabel,
  onPrimaryAction,
  isPrimaryLoading,
  secondaryLabel,
  onSecondaryAction,
  withIcon = true,
  isCentered = true,
  size = 520,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  className,
}: AlertDialogProps) {
  const IconComponent = THEME_ICON_COMPONENT[variant];
  const iconColor = THEME_ICON_COLOR[variant];
  const primaryVariant = variant === 'negative' ? 'negative' : 'primary';

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      size={size}
      shouldCloseOnClickOutside={shouldCloseOnClickOutside}
      shouldCloseOnEscape={shouldCloseOnEscape}
      className={`${styles.root} ${className ?? ''}`}
    >
      <div className={styles.headerRow}>
        <div className={styles.contentContainer}>
          {withIcon && (
            <ThemeIcon color={iconColor} size="32">
              <IconComponent size={16} stroke={2} />
            </ThemeIcon>
          )}
          <div className={styles.titleContainer}>
            <p className={styles.title}>{title}</p>
            {description && (
              <div className={styles.bodyContainer}>
                <p className={styles.bodyPrimary}>{description}</p>
                {secondaryText && (
                  <p className={styles.bodySecondary}>{secondaryText}</p>
                )}
              </div>
            )}
            {children && <div className={styles.extraContent}>{children}</div>}
          </div>
        </div>
        <CloseButton onClick={onClose} aria-label="Close" />
      </div>
      <div className={styles.footerContainer}>
        <div className={styles.buttonsRow}>
          {secondaryLabel && (
            <Button variant="tertiary" onClick={onSecondaryAction}>
              {secondaryLabel}
            </Button>
          )}
          <Button
            variant={primaryVariant}
            onClick={onPrimaryAction}
            loading={isPrimaryLoading}
          >
            {primaryLabel}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

AlertDialog.displayName = 'AlertDialog';
